/**
 * @module components/analysis/ScoreGrid
 * @description
 *   Per-category score cards plus music-only View panel powered by
 *   `results.scoreBreakdown`. Desktop (lg+): side-by-side equal height with
 *   animated open/close. Mobile: View replaces Technical Analysis.
 *   Signals/penalties use tooltip definitions from `./signalDefinitions`.
 *   Metric cards are a 2-col grid (md+); odd last card spans full width
 *   (revert to half width when a 6th free craft metric makes even pairs).
 *
 *   Music chrome: Technical Analysis subtitle shows
 *   `Evaluated as {scoredForGenreLabel}` (craft lane). Fallback is
 *   Parent · Sub from trendAnalysis. View header omits that line.
 *   Metric summaries may still start with “Scored for …” on older sessions
 *   — leave that prefix in place. Grade badges use getScoreBadgeTone so
 *   bands match number color (green / yellow Good / orange Fair / red Poor).
 *
 * @see docs/SCORE_BREAKDOWN.md
 * @see docs/GENRE_PACK_RESEARCH.md
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Settings, Minimize2, ArrowUp, ArrowDown } from 'lucide-react';
import { getScoreColor, getScoreLabel, getScoreBadgeTone } from '../../utils/scoreHelpers';
import { Card, SectionHeader, LockOverlay, IconTile, Badge } from '../ui';
import { getSignalDefinition, getPenaltyDefinition } from './signalDefinitions';
import LearnMoreTip from './LearnMoreTip';
import { formatMusicGenreDisplay } from '../../constants/musicGenreLabels';

const SCORE_COLORS = [
  'bg-gradient-to-r from-apple-blue to-apple-purple',
  'bg-gradient-to-r from-apple-purple to-apple-pink',
  'bg-gradient-to-r from-apple-green to-apple-teal',
  'bg-gradient-to-r from-apple-orange to-apple-yellow',
];

function formatCategoryLabel(category, craftType) {
  if (craftType === 'book') {
    return category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }
  return category;
}

/** Craft lane for Technical Analysis chrome. Prefer stamped pack label. */
function resolveCraftEvaluatedAsLabel(results) {
  const stamped = results?.scoreBreakdown?.scoredForGenreLabel;
  if (stamped && String(stamped).trim()) return String(stamped).trim();
  const trend = results?.trendAnalysis;
  if (!trend?.primaryGenre) return null;
  const fallback = formatMusicGenreDisplay(trend.primaryGenre, trend.primarySubgenre);
  return fallback && fallback !== '—' ? fallback : null;
}

function bandChipLabel(band) {
  if (band === 'low') return 'Too low';
  if (band === 'high') return 'Too high';
  return 'Sweet spot';
}

function bandBadgeTone(band) {
  // Sweet spot = grade green; Too low / Too high = deep orange (not yellow Fair, not worst red)
  return band === 'optimal' ? 'success' : 'orange';
}

function sortContributorsByImpact(contributors) {
  return [...(contributors || [])].sort((a, b) => {
    const aNonOptimal = a.band === 'optimal' ? 0 : 1;
    const bNonOptimal = b.band === 'optimal' ? 0 : 1;
    if (aNonOptimal !== bNonOptimal) return bNonOptimal - aNonOptimal;
    const aImpact = (a.weight || 0) * Math.abs((a.normalized || 0) - 70);
    const bImpact = (b.weight || 0) * Math.abs((b.normalized || 0) - 70);
    return bImpact - aImpact;
  });
}

const LEARN_MORE_PANEL_MS = 420;

/**
 * @param {object} props
 * @param {{ scores: object, scoreBreakdown?: object|null }} props.results
 * @param {'music'|'book'} props.craftType
 * @param {boolean} props.showPremiumContent
 */
export default function ScoreGrid({ results, craftType, showPremiumContent }) {
  const [expandedMetric, setExpandedMetric] = useState(null);
  const [visibleMetric, setVisibleMetric] = useState(null);
  const [panelEntered, setPanelEntered] = useState(false);
  const [panelHeight, setPanelHeight] = useState(null);
  const [sectionCollapsed, setSectionCollapsed] = useState(false);
  const technicalColumnRef = useRef(null);
  const breakdown = craftType === 'music' ? results.scoreBreakdown : null;
  const craftEvaluatedAsLabel =
    craftType === 'music' ? resolveCraftEvaluatedAsLabel(results) : null;
  const displayMetric =
    visibleMetric && breakdown?.[visibleMetric] ? visibleMetric : null;
  const expanded = displayMetric ? breakdown[displayMetric] : null;

  const scoreEntries = Object.entries(results.scores).filter(([category, score]) => {
    if (category === 'is_instrumental') return false;

    const isInstrumental =
      results.scores.is_instrumental === true ||
      results.scores.is_instrumental === 'true' ||
      results.scores.is_instrumental === 1 ||
      results.scores.is_instrumental === '1';

    if (isInstrumental && (category === 'vocals' || category === 'lyrics')) {
      return false;
    }

    return score !== null && score !== undefined && score !== 'N/A';
  });

  const expandedIndex = scoreEntries.findIndex(([category]) => category === displayMetric);
  const expandedDisplayScore =
    displayMetric != null ? results.scores[displayMetric] : null;
  const expandedLabel = displayMetric
    ? formatCategoryLabel(displayMetric, craftType)
    : '';

  const sortedSignals = useMemo(
    () => sortContributorsByImpact(expanded?.contributors),
    [expanded]
  );

  useEffect(() => {
    if (expandedMetric && breakdown?.[expandedMetric]) {
      setVisibleMetric(expandedMetric);
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setPanelEntered(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }

    setPanelEntered(false);
    const t = setTimeout(() => setVisibleMetric(null), LEARN_MORE_PANEL_MS);
    return () => clearTimeout(t);
  }, [expandedMetric, breakdown]);

  useEffect(() => {
    if (!panelEntered || !technicalColumnRef.current) {
      if (!displayMetric) setPanelHeight(null);
      return undefined;
    }

    const node = technicalColumnRef.current;
    const updateHeight = () => {
      const next = Math.round(node.getBoundingClientRect().height);
      setPanelHeight(next > 0 ? next : null);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [displayMetric, scoreEntries.length, panelEntered]);

  const technicalCard = (
    <Card className="animate-slide-up glow-effect-blue hover-lift transition-all duration-300 w-full h-full">
      <SectionHeader
        stacked={false}
        className={sectionCollapsed ? undefined : 'mb-8'}
        collapsed={sectionCollapsed}
        onToggleCollapse={() => {
          setSectionCollapsed((prev) => {
            if (!prev) setExpandedMetric(null);
            return !prev;
          });
        }}
        icon={<Settings className="h-6 w-6 text-white" />}
        title={craftType === 'music' ? 'Technical Analysis' : 'Literary Analysis'}
        titleClassName="font-display text-4xl font-bold text-gradient-blue tracking-tight"
        subtitle={
          craftType === 'music'
            ? craftEvaluatedAsLabel
              ? `Evaluated as ${craftEvaluatedAsLabel}.`
              : "Comprehensive evaluation of your track's technical aspects."
            : "Comprehensive evaluation of your book's literary aspects."
        }
      />

      {!sectionCollapsed ? (
      <>
      {/*
        Two-column metric grid. When the visible count is odd, the last card
        spans full width. If we add a 6th free craft metric (even pairs), drop
        the full-width span and keep every card at half width again.
      */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {scoreEntries.map(([category, score], index) => {
          const isLocked =
            !showPremiumContent &&
            (category === 'hit_potential' || category === 'market_potential');
          const displayCategory = formatCategoryLabel(category, craftType);
          const displayScore = score;
          const displayLabel = getScoreLabel(score);
          const metricBreakdown = breakdown?.[category];
          const canLearnMore =
            craftType === 'music' && !isLocked && metricBreakdown && typeof metricBreakdown === 'object';
          const isLastOdd =
            scoreEntries.length % 2 === 1 && index === scoreEntries.length - 1;

          return (
            <Card
              key={category}
              variant="inset"
              className={`hover:border-white/[0.12] transition-all duration-300 animate-slide-up relative ${
                isLocked ? 'overflow-hidden' : ''
              } ${expandedMetric === category ? 'ring-1 ring-apple-blue/40' : ''} ${
                isLastOdd ? 'md:col-span-2' : ''
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {isLocked && (
                <LockOverlay
                  compact
                  title=""
                  body="Upgrade to unlock"
                  tone="blue"
                  className="bg-critic-bg-deep/92"
                />
              )}
              <div className={`space-y-4 ${isLocked ? 'opacity-30 blur-sm pointer-events-none' : ''}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center space-x-3 min-w-0">
                    <IconTile size="sm" gradient={SCORE_COLORS[index % SCORE_COLORS.length]}>
                      <span className="text-white font-bold text-sm">
                        {displayCategory.charAt(0).toUpperCase()}
                      </span>
                    </IconTile>
                    <span className="font-display text-base font-semibold text-critic-text-primary capitalize truncate">
                      {displayCategory}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 shrink-0">
                    <span
                      className={`text-2xl font-bold tabular-nums ${
                        isLocked ? 'text-critic-text-muted' : getScoreColor(displayScore)
                      }`}
                    >
                      {isLocked ? '--' : displayScore === 'N/A' ? 'N/A' : `${displayScore}`}
                    </span>
                    <Badge
                      tone={isLocked ? 'default' : getScoreBadgeTone(displayScore)}
                      className={isLocked ? 'opacity-50' : ''}
                    >
                      {isLocked ? 'Locked' : displayLabel}
                    </Badge>
                  </div>
                </div>
                {displayScore !== 'N/A' && (
                  <div className="space-y-2">
                    <div className="score-bar">
                      <div
                        className={`score-fill ${isLocked ? 'bg-gray-600' : ''}`}
                        style={{ width: isLocked ? '50%' : `${displayScore}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-critic-text-muted font-normal">
                      <span>Poor</span>
                      <span>Excellent</span>
                    </div>
                  </div>
                )}
                {canLearnMore && (
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedMetric((current) => (current === category ? null : category))
                    }
                    className="text-sm font-medium text-apple-blue hover:text-apple-blue/80 underline-offset-2 hover:underline transition-colors"
                  >
                    {expandedMetric === category ? 'Hide details' : 'View'}
                  </button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
      </>
      ) : null}
    </Card>
  );

  const learnMoreCard = expanded ? (
    <Card className="glow-effect-blue hover-lift transition-all duration-300 relative w-full h-full flex flex-col overflow-hidden">
      <button
        type="button"
        onClick={() => setExpandedMetric(null)}
        className="absolute top-4 right-4 z-10 inline-flex items-center justify-center rounded-xl border border-white/[0.08] bg-black/30 p-2 text-critic-text-secondary hover:text-critic-text-primary hover:border-white/[0.16] transition-colors"
        aria-label="Minimize View"
      >
        <Minimize2 className="h-4 w-4" />
      </button>

      <div className="flex flex-col flex-1 min-h-0">
        <div className="shrink-0 space-y-5 pb-4">
          <div className="flex items-center justify-between gap-3 pr-12">
            <div className="flex items-center space-x-3 min-w-0">
              <IconTile
                size="sm"
                gradient={SCORE_COLORS[Math.max(0, expandedIndex) % SCORE_COLORS.length]}
              >
                <span className="text-white font-bold text-sm">
                  {expandedLabel.charAt(0).toUpperCase()}
                </span>
              </IconTile>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-critic-text-muted">View</p>
                <h3 className="font-display text-xl font-semibold text-critic-text-primary capitalize truncate">
                  {expandedLabel}
                </h3>
              </div>
            </div>
            <div className="flex items-center space-x-3 shrink-0">
              <span className={`text-2xl font-bold tabular-nums ${getScoreColor(expandedDisplayScore)}`}>
                {expandedDisplayScore}
              </span>
              <Badge tone={getScoreBadgeTone(expandedDisplayScore)}>
                {getScoreLabel(expandedDisplayScore)}
              </Badge>
            </div>
          </div>

          {expandedDisplayScore !== 'N/A' && (
            <div className="space-y-2">
              <div className="score-bar">
                <div className="score-fill" style={{ width: `${expandedDisplayScore}%` }} />
              </div>
              <div className="flex justify-between text-xs text-critic-text-muted font-normal">
                <span>Poor</span>
                <span>Excellent</span>
              </div>
            </div>
          )}

          {expanded.summary && (
            <p className="text-sm md:text-[0.95rem] leading-relaxed text-critic-text-primary">
              {expanded.summary}
            </p>
          )}

          {Array.isArray(expanded.topDrivers) && expanded.topDrivers.length > 0 && (
            <div className="space-y-2">
              <div>
                <h4 className="font-display text-sm font-semibold text-critic-text-primary">
                  Main factors
                </h4>
                <p className="text-xs text-critic-text-muted mt-0.5">
                  What pushed this score up or down.
                </p>
              </div>
              <ul className="space-y-2">
                {expanded.topDrivers.map((driver) => {
                  const helping = driver.direction === 'helping';
                  const Arrow = helping ? ArrowUp : ArrowDown;
                  return (
                    <li
                      key={driver.id}
                      className="text-sm text-critic-text-secondary flex items-center justify-between gap-3"
                    >
                      <span className="min-w-0 truncate">{driver.label}</span>
                      <Arrow
                        className={`h-4 w-4 shrink-0 ${
                          helping ? 'text-apple-green' : 'text-apple-orange'
                        }`}
                        aria-label={helping ? 'Helping' : 'Holding back'}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1">
          {sortedSignals.length > 0 && (
            <div className="space-y-1">
              <h4 className="font-display text-sm font-semibold text-critic-text-primary py-1">
                Signals
              </h4>
              <ul className="divide-y divide-white/[0.06]">
                {sortedSignals.map((c) => (
                  <li key={c.id} className="py-2.5 space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <LearnMoreTip
                        id={`signal-${c.id}`}
                        label={c.label}
                        definition={getSignalDefinition(c.id)}
                      />
                      <Badge tone={bandBadgeTone(c.band)} className="text-[10px] px-2 py-0.5 shrink-0">
                        {bandChipLabel(c.band)}
                      </Badge>
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-white/45"
                        style={{ width: `${c.normalized || 0}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(expanded.penalties) && expanded.penalties.length > 0 && (
            <div className="space-y-2 pb-2">
              <h4 className="font-display text-sm font-semibold text-critic-text-primary">Penalties</h4>
              <ul className="divide-y divide-white/[0.06]">
                {expanded.penalties.map((p) => (
                  <li
                    key={p.id}
                    className="py-2.5 flex items-center justify-between gap-3"
                  >
                    <LearnMoreTip
                      id={`penalty-${p.id}`}
                      label={p.label}
                      definition={getPenaltyDefinition(p.id)}
                      labelClassName="text-sm text-critic-text-secondary"
                    />
                    <span className="text-sm tabular-nums shrink-0 text-critic-text-secondary">
                      −{p.amount}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Card>
  ) : null;

  return (
    <div
      className={`flex flex-col lg:flex-row items-stretch transition-[gap] duration-500 ease-critic-out ${
        panelEntered && !sectionCollapsed ? 'gap-6 lg:gap-8' : 'gap-0'
      }`}
    >
      {/* Always flex-1 so open/close only resize via the sibling width transition — never remount/swap w-full ↔ flex-1 */}
      <div
        ref={technicalColumnRef}
        className={`min-w-0 flex-1 self-stretch ${
          panelEntered && !sectionCollapsed ? 'max-lg:hidden' : ''
        }`}
      >
        {technicalCard}
      </div>

      <div
        className={`min-w-0 overflow-hidden self-stretch lg:shrink-0 transition-[width,opacity,transform] duration-500 ease-critic-out ${
          panelEntered && !sectionCollapsed
            ? 'w-full opacity-100 translate-x-0 lg:w-[400px]'
            : 'w-0 opacity-0 translate-x-3 pointer-events-none'
        }`}
        style={
          panelHeight
            ? { ['--learn-more-panel-h']: `${panelHeight}px` }
            : undefined
        }
        aria-hidden={!panelEntered || sectionCollapsed}
      >
        <div className="w-full h-full lg:w-[400px] lg:h-[var(--learn-more-panel-h)]">
          {sectionCollapsed ? null : learnMoreCard}
        </div>
      </div>
    </div>
  );
}
